import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
    providedIn: "root"
})
export class SnackbarService {
    constructor(private snackBar: MatSnackBar, private translate: TranslateService) {}

    public showMessage(message: string, duration = 5000, isError = false): void {
        this.snackBar.open(message, "OK", {
            duration: isError ? 0 : duration,
            horizontalPosition: "right",
            verticalPosition: "top",
            panelClass: isError ? "msg-error" : null
        });
    }

    public showHttpMessage(statusCode: number, message?: any, hide401 = true, hide404 = true): void {
        if (hide401 && statusCode == 401) return;
        if (hide404 && statusCode == 404) return;

        this.snackBar.open(this.resolveHttpMessage(statusCode, message), "x", {
            duration: 5000,
            panelClass: "mat-primary"
        });
    }

    /**
     * Converte o erro recebido em uma mensagem amigável para o usuário.
     * Usa a mensagem enviada pela API quando ela é legível; caso contrário
     * (texto técnico do Angular, objeto de validação ou vazio) exibe uma
     * mensagem genérica traduzida de acordo com o status HTTP.
     */
    private resolveHttpMessage(statusCode: number, message: any): string {
        return this.extractServerMessage(message) ?? this.genericMessageForStatus(statusCode);
    }

    private extractServerMessage(message: any): string | null {
        if (message === null || message === undefined) return null;

        // Corpo da resposta como objeto (ProblemDetails / ModelState do ASP.NET, etc.)
        if (typeof message === "object") {
            const errors = message.errors;
            if (errors && typeof errors === "object") {
                const firstList = Object.keys(errors)
                    .map((key) => errors[key])
                    .find((value) => Array.isArray(value) && value.length && typeof value[0] === "string");
                if (Array.isArray(firstList)) return firstList[0];
            }

            const candidate = message.message ?? message.title ?? message.detail ?? message.error;
            if (typeof candidate !== "string") return null;
            message = candidate;
        }

        if (typeof message !== "string") return null;

        const trimmed = message.trim();
        if (!trimmed || this.isTechnicalMessage(trimmed)) return null;

        return trimmed;
    }

    /** Detecta o texto técnico padrão do HttpErrorResponse do Angular (não deve ser exibido ao usuário). */
    private isTechnicalMessage(message: string): boolean {
        return /^Http failure response/i.test(message) || message.includes("://");
    }

    private genericMessageForStatus(statusCode: number): string {
        let key: string;
        if (statusCode === 0) key = "MESSAGES.HTTP_ERROR.NETWORK";
        else if (statusCode === 400 || statusCode === 422) key = "MESSAGES.HTTP_ERROR.BAD_REQUEST";
        else if (statusCode === 403) key = "MESSAGES.HTTP_ERROR.FORBIDDEN";
        else if (statusCode >= 500) key = "MESSAGES.HTTP_ERROR.SERVER";
        else key = "MESSAGES.HTTP_ERROR.GENERIC";

        const translated = this.translate.instant(key);
        // Se a chave ainda não estiver carregada, o ngx-translate devolve a própria chave.
        if (translated && translated !== key) return translated;

        const fallback = this.translate.instant("MESSAGES.ERROR_UPDATED");
        return fallback && fallback !== "MESSAGES.ERROR_UPDATED" ? fallback : "Não foi possível concluir a operação.";
    }
}
